/**
 * Standalone monkey-patch for RequestScheduler and Tileset2D
 * This adds functionality from https://github.com/visgl/loaders.gl/pull/3280 & integrates it into Tileset2D.setOptions()
 *
 * TODO: Remove this file once loaders.gl 4.4 is released with setProps()
 */

import { RequestScheduler } from '@loaders.gl/loader-utils'
import { _Tileset2D as Tileset2D } from '@deck.gl/geo-layers'

type RequestSchedulerProps = {
  throttleRequests?: boolean
  maxRequests?: number
  debounceTime?: number
}

// Type for RequestScheduler with the patched methods and properties
interface PatchedRequestScheduler {
  props: RequestSchedulerProps
  setProps(props: RequestSchedulerProps): void
}

// Type for Tileset2D with internal properties we need to access
interface PatchedTileset2D {
  _requestScheduler: PatchedRequestScheduler
  opts: {
    maxRequests: number
    debounceTime: number
  }
  setOptions(opts: { debounceTime?: number }): void
}

interface PatchedSetOptions {
  (opts: { debounceTime?: number }): void
  __patched?: boolean
}

/**
 * Adds setProps method to RequestScheduler prototype if it doesn't already exist
 * This implementation matches https://github.com/visgl/loaders.gl/pull/3280
 */
function patchRequestSchedulerPrototype(): void {
  // Check if already patched or if the method exists in the library
  if ('setProps' in RequestScheduler.prototype) {
    return
  }

  // Add setProps method to the prototype
  // This matches the exact implementation from loaders.gl PR #3280
  const prototype = RequestScheduler.prototype as unknown as PatchedRequestScheduler
  prototype.setProps = function (this: PatchedRequestScheduler, props: RequestSchedulerProps): void {
    if (props.throttleRequests !== undefined) {
      this.props.throttleRequests = props.throttleRequests
    }
    if (props.maxRequests !== undefined) {
      this.props.maxRequests = props.maxRequests
    }
    if (props.debounceTime !== undefined) {
      this.props.debounceTime = props.debounceTime
    }
  }
}

/**
 * Patches Tileset2D.setOptions to automatically update the request scheduler
 * when debounceTime changes
 */
function patchTileset2DSetOptions(): void {
  const originalSetOptions = Tileset2D.prototype.setOptions as unknown as PatchedSetOptions

  // Check if already patched
  if (originalSetOptions.__patched) {
    return
  }

  const patchedSetOptions: PatchedSetOptions = function (this: unknown, opts: { debounceTime?: number }): void {
    const typedThis = this as PatchedTileset2D

    // Call the original setOptions
    originalSetOptions.call(this, opts)

    // Update request scheduler if debounceTime changed
    // Note: This requires RequestScheduler.setProps() to be available
    if (typeof typedThis._requestScheduler?.setProps === 'function' && opts.debounceTime !== undefined) {
      typedThis._requestScheduler.setProps({
        debounceTime: opts.debounceTime,
        throttleRequests: typedThis.opts.maxRequests > 0 || opts.debounceTime > 0
      })
    }
  }

  // Mark as patched to avoid double-patching
  patchedSetOptions.__patched = true
  Tileset2D.prototype.setOptions = patchedSetOptions as typeof Tileset2D.prototype.setOptions
}

/**
 * Applies both RequestScheduler and Tileset2D patches
 * Call this once at application startup, before creating any Tileset2D instances
 */
export function patchRequestScheduler(): void {
  patchRequestSchedulerPrototype()
  patchTileset2DSetOptions()
}
